import { UseSearchResult } from "@saleor/hooks/makeSearch";
import { findValueInEnum, maybe } from "@saleor/misc";
import {
  SearchBrands,
  SearchBrandsVariables
} from "@saleor/searches/types/SearchBrands";
import {
  SearchMalls,
  SearchMallsVariables
} from "@saleor/searches/types/SearchMalls";
import { PayoutStatusEnum } from "@saleor/types/globalTypes";
import moment from "moment";

import { IFilterElement } from "../../../../components/Filter";
import {
  createFilterTabUtils,
  createFilterUtils,
  dedupeFilter,
  getGteLteVariables,
  getMinMaxQueryParam,
  getMultipleValueQueryParam
} from "../../../../utils/filters";
import { removeBase64Name } from "../../../utils/misc";
import {
  PayoutsFilterKeys,
  PayoutsListFilterOpts
} from "../../components/PayoutsListPage/filters";
import {
  InitialPayoutFilterData_brands_edges_node,
  InitialPayoutFilterData_malls_edges_node
} from "../../types/InitialPayoutFilterData";
import {
  PayoutsListUrlFilters,
  PayoutsListUrlFiltersEnum,
  PayoutsListUrlFiltersWithMultipleValues,
  PayoutsUrlQueryParams
} from "../../urls";
export const Payouts_FILTERS_KEY = "PayoutsFilters";

export function getFilterOpts(
  params: PayoutsListUrlFilters,
  malls: {
    initial: InitialPayoutFilterData_malls_edges_node[];
    search: UseSearchResult<SearchMalls, SearchMallsVariables>;
  },
  brand: {
    initial: InitialPayoutFilterData_brands_edges_node[];
    search: UseSearchResult<SearchBrands, SearchBrandsVariables>;
  }
): PayoutsListFilterOpts {
  return {
    brand: {
      active: !!params.brand,
      choices: maybe(
        () =>
          brand.search.result.data.search.edges.map(edge => ({
            label: edge.node.name,
            value: edge.node.id
          })),
        []
      ),
      displayValues: [],
      hasMore: maybe(
        () => brand.search.result.data.search.pageInfo.hasNextPage,
        false
      ),
      initialSearch: "",
      loading: false,
      onFetchMore: brand.search.loadMore,
      onSearchChange: brand.search.search,
      value: maybe(() => dedupeFilter(params.mall), [])
    },
    coverageDate: {
      active: maybe(
        () =>
          [params.coverageFrom, params.coverageTo].some(
            field => field !== undefined
          ),
        false
      ),
      value: {
        max: maybe(() => params.coverageTo, ""),
        min: maybe(() => params.coverageFrom, "")
      }
    },
    generatedAt: {
      active: maybe(
        () =>
          [params.generatedFrom, params.generatedTo].some(
            field => field !== undefined
          ),
        false
      ),
      value: {
        max: maybe(() => params.generatedTo, ""),
        min: maybe(() => params.generatedFrom, "")
      }
    },
    mall: {
      active: !!params.mall,
      choices: maybe(
        () =>
          malls.search.result.data.search.edges.map(edge => ({
            label: edge.node.name,
            value: edge.node.id
          })),
        []
      ),
      displayValues: [],
      hasMore: maybe(
        () => malls.search.result.data.search.pageInfo.hasNextPage,
        false
      ),
      initialSearch: "",
      loading: malls.search.result.loading,
      onFetchMore: malls.search.loadMore,
      onSearchChange: malls.search.search,
      value: maybe(() => dedupeFilter(params.mall), [])
    }
  };
}

export function getFilterVariables(params: PayoutsListUrlFilters) {
  const newDate = new Date();
  const endDay = newDate.getDate();
  const endMonth = newDate.getMonth() + 1;
  const endYear = newDate.getFullYear();

  const startDate = moment(newDate.setMonth(newDate.getMonth() - 3)).format(
    "YYYY-MM-DD"
  );
  const endDate = moment(`${endYear}-${endMonth}-${endDay} 00:00:00`)
    .add(1, "days")
    .format("YYYY-MM-DD HH:mm:ssZZ");

  return {
    brandId: removeBase64Name(
      "Brand:",
      params.brand !== undefined ? params.brand : []
    ),
    coverageDate: getGteLteVariables({
      gte: params.coverageFrom
        ? moment(`${params.coverageFrom} 00:00:00`).format(
            "YYYY-MM-DD HH:mm:ssZZ"
          )
        : null,
      lte: params.coverageTo
        ? moment(`${params.coverageTo} 00:00:00`)
            .add(1, "days")
            .format("YYYY-MM-DD HH:mm:ssZZ")
        : null
    }),
    generatedAt: getGteLteVariables({
      gte: params.generatedFrom
        ? moment(`${params.generatedFrom} 00:00:00`).format(
            "YYYY-MM-DD HH:mm:ssZZ"
          )
        : moment(`${startDate} 00:00:00`).format("YYYY-MM-DD HH:mm:ssZZ"),
      lte: params.generatedTo
        ? moment(`${params.generatedTo} 00:00:00`)
            .add(1, "days")
            .format("YYYY-MM-DD HH:mm:ssZZ")
        : endDate
    }),
    mallId: removeBase64Name(
      "Mall:",
      params.mall !== undefined ? params.mall : []
    ),
    payoutStatus:
      params.payoutStatus !== undefined
        ? findValueInEnum(params.payoutStatus, PayoutStatusEnum) ===
          PayoutStatusEnum.SCHEDULE_FOR_PAYOUT
          ? [PayoutStatusEnum.SCHEDULE_FOR_PAYOUT, PayoutStatusEnum.RETURNED]
          : findValueInEnum(params.payoutStatus, PayoutStatusEnum) ===
            PayoutStatusEnum.PAYMENT_PROCESSING
          ? [
              PayoutStatusEnum.PAYMENT_PROCESSING,
              PayoutStatusEnum.READY_FOR_PAYMENT
            ]
          : [findValueInEnum(params.payoutStatus, PayoutStatusEnum)]
        : null,
    search: params.query
  };
}

export function getFilterQueryParam(
  filter: IFilterElement<PayoutsFilterKeys>,
  params: PayoutsListUrlFilters
): PayoutsListUrlFilters {
  const { active, group, name, value } = filter;

  if (!!group) {
    const rest = params && params[group] ? params[group] : undefined;

    return {
      [group]: active
        ? {
            ...(rest === undefined ? {} : rest),
            [name]: value
          }
        : rest
    };
  }

  switch (name) {
    case PayoutsFilterKeys.mall:
      return getMultipleValueQueryParam(
        filter,
        PayoutsListUrlFiltersWithMultipleValues.mall
      );
    case PayoutsFilterKeys.brand:
      return getMultipleValueQueryParam(
        filter,
        PayoutsListUrlFiltersWithMultipleValues.brand
      );
    case PayoutsFilterKeys.generatedAt:
      return getMinMaxQueryParam(
        filter,
        PayoutsListUrlFiltersEnum.generatedFrom,
        PayoutsListUrlFiltersEnum.generatedTo
      );
    case PayoutsFilterKeys.coverageDate:
      return getMinMaxQueryParam(
        filter,
        PayoutsListUrlFiltersEnum.coverageFrom,
        PayoutsListUrlFiltersEnum.coverageTo
      );
  }
}

export const {
  deleteFilterTab,
  getFilterTabs,
  saveFilterTab
} = createFilterTabUtils<PayoutsListUrlFilters>(Payouts_FILTERS_KEY);

export const { areFiltersApplied, getActiveFilters } = createFilterUtils<
  PayoutsUrlQueryParams,
  PayoutsListUrlFilters
>({
  ...PayoutsListUrlFiltersEnum,
  ...PayoutsListUrlFiltersWithMultipleValues
});
